/**
 * PublishGateOverlay
 *
 * Renderizado por cima do conteúdo da aba Canais quando o agente ainda não
 * passou pela rotina de publicação (`!is_published`). Mostra preview do
 * conteúdo desabilitado por baixo + CTA centralizado "Publicar agente".
 *
 * Quando o usuário clica "Publicar agente", chama `usePublishValidation`
 * — mesma rotina do botão Publicar do canvas e do PublishChannelModal.
 *
 * Após sucesso, o `currentAgent.is_published` no AgentsFlowContext atualiza,
 * o overlay desmonta, e o usuário interage normalmente com a aba.
 */

import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconRocket, IconLock } from '@tabler/icons-react';
import usePublishValidation from '../../../hooks/usePublishValidation';

const PublishGateOverlay = ({ agentId }) => {
  const { validateAndPublish } = usePublishValidation();
  const [publishing, setPublishing] = useState(false);

  const isNewAgent = !agentId || agentId === 'novo';

  const handlePublish = async () => {
    if (isNewAgent || publishing) return; // guard contra double-click + agente novo
    setPublishing(true);
    try {
      // Sem nodes/edges: hook busca o graph SALVO no backend.
      await validateAndPublish(agentId, undefined, undefined);
      // Sucesso: o context atualiza is_published e o overlay desmonta
      // automaticamente. Nada a fazer aqui.
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="channels-tab__publish-overlay">
      <div className="channels-tab__publish-card">
        <div className="channels-tab__publish-icon">
          <IconLock size={28} />
        </div>
        <h3 className="channels-tab__publish-title">
          {isNewAgent
            ? 'Salve o agente antes de conectar canais'
            : 'Publique seu agente para conectar canais'}
        </h3>
        <p className="channels-tab__publish-description">
          {isNewAgent
            ? 'Este agente ainda não foi salvo. Volte ao canvas, salve o fluxo e publique para liberar a aba Canais.'
            : 'Antes de conectar canais de comunicação, é preciso publicar o agente uma vez para validar o fluxo e ativar a conta na plataforma.'}
        </p>
        <button
          type="button"
          className="channels-tab__publish-btn"
          onClick={handlePublish}
          disabled={publishing || isNewAgent}
          title={isNewAgent ? 'Salve o agente primeiro' : undefined}
        >
          {publishing ? (
            <>
              <Spinner animation="border" size="sm" />
              Publicando...
            </>
          ) : (
            <>
              <IconRocket size={18} />
              Publicar agente
            </>
          )}
        </button>
        {!isNewAgent && (
          <p className="channels-tab__publish-hint">
            A publicação valida o fluxo, capacidades, modelo de IA e aplica os
            limites do seu plano. Após publicar, todos os canais ficam disponíveis.
          </p>
        )}
      </div>
    </div>
  );
};

export default PublishGateOverlay;
